import { type Database } from "@fitness-app/supabase";

export type ClientRatingSnapshotEntity = Database["public"]["Tables"]["client_rating_snapshot"]["Row"];

export type ClientRatingBreakdownKey =
  | "workouts"
  | "bodyMeasurements"
  | "bodyPhotos"
  | "weightMeasurements"
  | "mealsTracked"
  | "surveyAnswers"
  | "consultations"
  | "messages"
  | "logins"
  // TRE-1367 — native habit / challenge completions feed the global rating (ADR
  // docs/challenges/adr-habits-challenges-feed-rating.md). Read from the source tables on every recompute.
  | "habits"
  | "challenges";

export type ClientRatingBreakdown = Record<ClientRatingBreakdownKey, number>;

export type ClientRatingWeights = Record<ClientRatingBreakdownKey, number>;

/** Per-day count caps; missing key ⇒ uncapped for that category. */
export type ClientRatingDailyLimitKey = "messages" | "logins" | "habits" | "challenges";

export type ClientRatingDailyLimits = Partial<Record<ClientRatingDailyLimitKey, number>>;

export type ClientRatingLevel = {
  level: number;
  minPoints: number;
};

export enum ClientRatingScopeType {
  Global = "global",
  Challenge = "challenge",
}

export enum ClientRatingCalculationMode {
  Global = "global",
  Range = "range",
}

export type ClientRatingDateRange = {
  start: string;
  end: string;
};

export const GLOBAL_CLIENT_RATING_SCOPE_ID = "global";

export type ClientRatingConfigData = {
  weights: ClientRatingWeights;
  dailyLimits: ClientRatingDailyLimits;
  levels: ClientRatingLevel[];
  recentBonusWindowDays: number;
  recentBonusMultiplier: number;
};

export type ClientRatingConfig = {
  id: string;
  type: "client_rating";
  data: ClientRatingConfigData;
  version: number | null;
};

export interface ClientRatingSnapshot extends Omit<ClientRatingSnapshotEntity, "breakdown"> {
  breakdown: ClientRatingBreakdown;
}

export const defaultClientRatingBreakdown: ClientRatingBreakdown = {
  workouts: 0,
  bodyMeasurements: 0,
  bodyPhotos: 0,
  weightMeasurements: 0,
  mealsTracked: 0,
  surveyAnswers: 0,
  consultations: 0,
  messages: 0,
  logins: 0,
  habits: 0,
  challenges: 0,
};

export const defaultClientRatingConfigData: ClientRatingConfigData = {
  weights: {
    workouts: 10,
    bodyMeasurements: 8,
    bodyPhotos: 12,
    weightMeasurements: 5,
    mealsTracked: 2,
    surveyAnswers: 8,
    consultations: 10,
    messages: 1,
    logins: 1,
    habits: 3,
    challenges: 5,
  },
  dailyLimits: {
    messages: 5,
    logins: 1,
    habits: 5,
    challenges: 5,
  },
  levels: [
    { level: 1, minPoints: 0 },
    { level: 2, minPoints: 150 },
    { level: 3, minPoints: 400 },
    { level: 4, minPoints: 800 },
    { level: 5, minPoints: 1600 },
    { level: 6, minPoints: 3000 },
    { level: 7, minPoints: 6000 },
    { level: 8, minPoints: 11000 },
    { level: 9, minPoints: 26000 },
    { level: 10, minPoints: 45000 },
  ],
  recentBonusWindowDays: 30,
  recentBonusMultiplier: 1,
};
